import React from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PentagonOutlinedIcon from "@mui/icons-material/PentagonOutlined";
import { DEFAULT_TAB_SHAPE, ShapePreview, TabShape, TabShapeEditor } from "./TabShapeEditor";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
  HomeTab,
  HomeTabEntry,
  PickOption,
  addHomeTabEntries,
  createHomeTab,
  deleteHomeTab,
  getHomeTabEntries,
  listHomeTabs,
  removeHomeTabEntry,
  reorderHomeTabEntries,
  reorderHomeTabs,
  searchItems,
  searchStores,
  updateFoodTabTitle,
  updateTabShape,
  updateHomeTab,
  uploadFoodTabImage,
  uploadHomeTabImage,
} from "../../../service/BuyerConfiguration/BuyerAppDashboard/homeTabsService";

const GREEN = "#00B14F";

const errMsg = (e: any, fallback: string) =>
  e?.response?.data?.message || fallback;

/** Square preview of a tab image on the app's green, like the app shows it. */
const Thumb: React.FC<{ url?: string | null; label: string }> = ({ url, label }) => (
  <Box
    sx={{
      width: 64,
      height: 64,
      borderRadius: 2.5,
      bgcolor: GREEN,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      overflow: "hidden",
      flexShrink: 0,
    }}
  >
    {url ? (
      <img src={url} alt={label} style={{ width: "88%", height: "88%", objectFit: "contain" }} />
    ) : (
      <Typography sx={{ color: "#fff", fontSize: 11, textAlign: "center", px: 0.5 }}>
        No image
      </Typography>
    )}
  </Box>
);

const ImagePickButton: React.FC<{
  busy: boolean;
  onFile: (f: File) => void;
  label?: string;
}> = ({ busy, onFile, label = "Upload image" }) => (
  <Button
    component="label"
    size="small"
    variant="outlined"
    disabled={busy}
    sx={{ textTransform: "none", borderColor: GREEN, color: GREEN }}
  >
    {busy ? "Uploading…" : label}
    <input
      hidden
      type="file"
      accept="image/png,image/webp,image/jpeg"
      onChange={(e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (f) onFile(f);
      }}
    />
  </Button>
);

/* ---------------- entries dialog: restaurants + dishes ---------------- */

const EntriesDialog: React.FC<{
  tab: HomeTab;
  onClose: () => void;
  onToast: (msg: string, ok: boolean) => void;
}> = ({ tab, onClose, onToast }) => {
  const [kind, setKind] = React.useState<"store" | "item">("store");
  const [entries, setEntries] = React.useState<HomeTabEntry[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [query, setQuery] = React.useState("");
  const [options, setOptions] = React.useState<PickOption[]>([]);
  const [searching, setSearching] = React.useState(false);
  const [picked, setPicked] = React.useState<PickOption[]>([]);
  const [saving, setSaving] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      setEntries(await getHomeTabEntries(tab.id));
    } catch (e) {
      onToast(errMsg(e, "Could not load this tab's list"), false);
    } finally {
      setLoading(false);
    }
  }, [tab.id, onToast]);

  React.useEffect(() => {
    load();
  }, [load]);

  // Debounced search for the active kind.
  React.useEffect(() => {
    let live = true;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const rows = kind === "store" ? await searchStores(query) : await searchItems(query);
        if (live) setOptions(rows);
      } catch {
        if (live) setOptions([]);
      } finally {
        if (live) setSearching(false);
      }
    }, 300);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [query, kind]);

  const visible = entries.filter((e) => e.entity_type === kind);
  const already = new Set(visible.map((e) => e.entity_id));

  const add = async () => {
    if (!picked.length) return;
    setSaving(true);
    try {
      setEntries(await addHomeTabEntries(tab.id, kind, picked.map((p) => p.id)));
      setPicked([]);
      onToast(`Added to ${tab.title}`, true);
    } catch (e) {
      onToast(errMsg(e, "Could not add"), false);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (entry: HomeTabEntry) => {
    try {
      await removeHomeTabEntry(tab.id, entry.id);
      setEntries((prev) => prev.filter((e) => e.id !== entry.id));
    } catch (e) {
      onToast(errMsg(e, "Could not remove"), false);
    }
  };

  const move = async (index: number, dir: -1 | 1) => {
    const list = [...visible];
    const j = index + dir;
    if (j < 0 || j >= list.length) return;
    [list[index], list[j]] = [list[j], list[index]];
    try {
      setEntries(
        await reorderHomeTabEntries(
          tab.id,
          list.map((e, i) => ({ entry_id: e.id, sequence: i + 1 })),
        ),
      );
    } catch (e) {
      onToast(errMsg(e, "Could not reorder"), false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700 }}>{tab.title}: restaurants & dishes</DialogTitle>
      <DialogContent dividers>
        <Tabs
          value={kind}
          onChange={(_, v) => {
            setKind(v);
            setPicked([]);
            setQuery("");
          }}
          sx={{ mb: 2, "& .Mui-selected": { color: `${GREEN} !important` }, "& .MuiTabs-indicator": { bgcolor: GREEN } }}
        >
          <Tab value="store" label={`Restaurants (${entries.filter((e) => e.entity_type === "store").length})`} sx={{ textTransform: "none" }} />
          <Tab value="item" label={`Dishes (${entries.filter((e) => e.entity_type === "item").length})`} sx={{ textTransform: "none" }} />
        </Tabs>

        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Autocomplete
            multiple
            fullWidth
            size="small"
            options={options.filter((o) => !already.has(o.id))}
            value={picked}
            loading={searching}
            filterOptions={(x) => x}
            onChange={(_, v) => setPicked(v)}
            onInputChange={(_, v, reason) => {
              if (reason === "input") setQuery(v);
            }}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            getOptionLabel={(o) => (o.store_name ? `${o.name} · ${o.store_name}` : o.name)}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={kind === "store" ? "Search restaurants" : "Search dishes"}
              />
            )}
          />
          <Button
            variant="contained"
            disabled={!picked.length || saving}
            onClick={add}
            sx={{ bgcolor: GREEN, "&:hover": { bgcolor: "#0a7a3c" }, textTransform: "none", whiteSpace: "nowrap" }}
          >
            {saving ? "Adding…" : "Add"}
          </Button>
        </Stack>

        <Box sx={{ mt: 2 }}>
          {loading ? (
            <Box sx={{ py: 3, textAlign: "center" }}>
              <CircularProgress size={22} sx={{ color: GREEN }} />
            </Box>
          ) : visible.length === 0 ? (
            <Typography sx={{ color: "text.secondary", fontSize: 14, py: 2 }}>
              {kind === "store"
                ? "No restaurants yet. Search above to add the ones this tab should list."
                : "No dishes yet. Search above to add the ones this tab should list."}
            </Typography>
          ) : (
            visible.map((e, i) => (
              <Stack
                key={e.id}
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ py: 1, borderBottom: "1px solid", borderColor: "divider" }}
              >
                <Typography sx={{ width: 22, color: "text.secondary", fontSize: 13 }}>{i + 1}</Typography>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }} noWrap>
                    {e.name ?? `#${e.entity_id}`}
                  </Typography>
                  {(e.store_name || e.missing) && (
                    <Typography sx={{ fontSize: 12, color: e.missing ? "error.main" : "text.secondary" }} noWrap>
                      {e.missing ? "Deleted from the catalogue. Not shown in the app." : e.store_name}
                    </Typography>
                  )}
                </Box>
                <IconButton size="small" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Move up">
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" disabled={i === visible.length - 1} onClick={() => move(i, 1)} aria-label="Move down">
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => remove(e)} aria-label="Remove">
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none", color: GREEN }}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ------------------------------ main ------------------------------ */

export const HomeTabsManager: React.FC<{
  onToast: (msg: string, ok: boolean) => void;
}> = ({ onToast }) => {
  const [tabs, setTabs] = React.useState<HomeTab[]>([]);
  const [foodImage, setFoodImage] = React.useState<string | null>(null);
  const [foodTitle, setFoodTitle] = React.useState("Food");
  const [maxTabs, setMaxTabs] = React.useState(3);
  const [editingFood, setEditingFood] = React.useState<string | null>(null);
  const [shape, setShape] = React.useState<TabShape>(DEFAULT_TAB_SHAPE);
  const [shapeOpen, setShapeOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState<string | null>(null);
  const [newTitle, setNewTitle] = React.useState("");
  const [editing, setEditing] = React.useState<{ id: number; title: string } | null>(null);
  const [entriesFor, setEntriesFor] = React.useState<HomeTab | null>(null);
  const [confirmDelete, setConfirmDelete] = React.useState<HomeTab | null>(null);

  const load = React.useCallback(async () => {
    try {
      const res = await listHomeTabs();
      setTabs(res.tabs);
      setFoodImage(res.food_image_url);
      setFoodTitle(res.food_title);
      setMaxTabs(res.max_tabs);
      if (res.shape?.points?.length) setShape(res.shape as TabShape);
    } catch (e) {
      onToast(errMsg(e, "Could not load home tabs"), false);
    } finally {
      setLoading(false);
    }
  }, [onToast]);

  React.useEffect(() => {
    load();
  }, [load]);

  const run = async (key: string, fn: () => Promise<any>, ok?: string) => {
    setBusy(key);
    try {
      await fn();
      if (ok) onToast(ok, true);
      await load();
    } catch (e) {
      onToast(errMsg(e, "Something went wrong. Try again."), false);
    } finally {
      setBusy(null);
    }
  };

  const move = (index: number, dir: -1 | 1) => {
    const j = index + dir;
    if (j < 0 || j >= tabs.length) return;
    const next = [...tabs];
    [next[index], next[j]] = [next[j], next[index]];
    setTabs(next);
    run("reorder", () => reorderHomeTabs(next.map((t) => t.id)));
  };

  if (loading) {
    return (
      <Box sx={{ py: 2 }}>
        <CircularProgress size={22} sx={{ color: GREEN }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Card shape: shared by all four cards in the app. */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "flex-start", sm: "center" }}
        sx={{ pb: 1.5, borderBottom: "1px solid", borderColor: "divider" }}
      >
        <ShapePreview
          shape={shape}
          width={220}
          cards={[
            { title: foodTitle, image: foodImage },
            ...tabs.map((t) => ({ title: t.title, image: t.image_url })),
          ]}
        />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 700 }}>Card shape</Typography>
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            One shape for all four cards. Changes reach open apps within seconds.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<PentagonOutlinedIcon />}
          onClick={() => setShapeOpen(true)}
          sx={{ textTransform: "none", borderColor: GREEN, color: GREEN }}
        >
          Shape
        </Button>
      </Stack>

      <TabShapeEditor
        open={shapeOpen}
        initial={shape}
        saving={busy === "shape"}
        cards={[
          { title: foodTitle, image: foodImage },
          ...tabs.map((t) => ({ title: t.title, image: t.image_url })),
        ]}
        onClose={() => setShapeOpen(false)}
        onApply={(next) =>
          run("shape", () => updateTabShape(next), "Shape applied. Open apps update in a few seconds.").then(() =>
            setShapeOpen(false),
          )
        }
      />

      {/* Food: fixed first tab, only its image is editable. */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
        <Thumb url={foodImage} label={foodTitle} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {editingFood !== null ? (
            <Stack direction="row" spacing={1}>
              <TextField
                size="small"
                value={editingFood}
                inputProps={{ maxLength: 60 }}
                onChange={(e) => setEditingFood(e.target.value)}
              />
              <Button
                size="small"
                disabled={!editingFood.trim() || busy === "food-title"}
                sx={{ textTransform: "none", color: GREEN }}
                onClick={() =>
                  run("food-title", () => updateFoodTabTitle(editingFood.trim()), "Name saved").then(() =>
                    setEditingFood(null),
                  )
                }
              >
                Save
              </Button>
              <Button size="small" sx={{ textTransform: "none" }} onClick={() => setEditingFood(null)}>
                Cancel
              </Button>
            </Stack>
          ) : (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography sx={{ fontWeight: 700 }}>{foodTitle}</Typography>
              <IconButton size="small" onClick={() => setEditingFood(foodTitle)} aria-label="Rename first tab">
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
              <Chip size="small" label="Always first" />
            </Stack>
          )}
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            The home screen itself. Upload a cut-out PNG (transparent background).
          </Typography>
        </Box>
        <ImagePickButton
          busy={busy === "food"}
          onFile={(f) => run("food", () => uploadFoodTabImage(f), "Food image updated")}
        />
      </Stack>

      {tabs.map((t, i) => (
        <Stack
          key={t.id}
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", sm: "center" }}
          sx={{ py: 1.5, borderBottom: "1px solid", borderColor: "divider", opacity: t.status ? 1 : 0.55 }}
        >
          <Thumb url={t.image_url} label={t.title} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {editing?.id === t.id ? (
              <Stack direction="row" spacing={1}>
                <TextField
                  size="small"
                  value={editing.title}
                  inputProps={{ maxLength: 60 }}
                  onChange={(e) => setEditing({ id: t.id, title: e.target.value })}
                />
                <Button
                  size="small"
                  disabled={!editing.title.trim()}
                  sx={{ textTransform: "none", color: GREEN }}
                  onClick={() =>
                    run("title", () => updateHomeTab(t.id, { title: editing.title.trim() }), "Name saved").then(() =>
                      setEditing(null),
                    )
                  }
                >
                  Save
                </Button>
                <Button size="small" sx={{ textTransform: "none" }} onClick={() => setEditing(null)}>
                  Cancel
                </Button>
              </Stack>
            ) : (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography sx={{ fontWeight: 700 }} noWrap>
                  {t.title}
                </Typography>
                <IconButton size="small" onClick={() => setEditing({ id: t.id, title: t.title })} aria-label="Rename">
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Stack>
            )}
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
              {t.restaurant_count ?? 0} restaurants · {t.dish_count ?? 0} dishes
              {!t.image_url && " · needs an image"}
            </Typography>
          </Box>

          <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
            <Tooltip title={t.status ? "Shown in the app" : "Hidden from the app"}>
              <Switch
                size="small"
                checked={t.status}
                onChange={(e) =>
                  run(`status-${t.id}`, () => updateHomeTab(t.id, { status: e.target.checked }))
                }
                sx={{ "& .Mui-checked": { color: `${GREEN} !important` }, "& .Mui-checked + .MuiSwitch-track": { bgcolor: `${GREEN} !important` } }}
              />
            </Tooltip>
            <ImagePickButton
              busy={busy === `img-${t.id}`}
              label={t.image_url ? "Change image" : "Upload image"}
              onFile={(f) => run(`img-${t.id}`, () => uploadHomeTabImage(t.id, f), "Image updated")}
            />
            <Button
              size="small"
              variant="contained"
              onClick={() => setEntriesFor(t)}
              sx={{ bgcolor: GREEN, "&:hover": { bgcolor: "#0a7a3c" }, textTransform: "none" }}
            >
              Restaurants & dishes
            </Button>
            <IconButton size="small" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Move up">
              <ArrowUpwardIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" disabled={i === tabs.length - 1} onClick={() => move(i, 1)} aria-label="Move down">
              <ArrowDownwardIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => setConfirmDelete(t)} aria-label="Delete tab">
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
      ))}

      {tabs.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Only {foodTitle} shows in the app right now. Add a tab below, e.g. “Delicious Biryani”.
        </Alert>
      )}

      {tabs.length >= maxTabs ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          All {maxTabs} tabs are in use, so the app shows {maxTabs + 1} cards. Delete a tab to add a different one.
        </Alert>
      ) : (
      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="New tab name, e.g. Cool Beverages"
          value={newTitle}
          inputProps={{ maxLength: 60 }}
          onChange={(e) => setNewTitle(e.target.value)}
        />
        <Button
          variant="contained"
          disabled={!newTitle.trim() || busy === "create"}
          onClick={() =>
            run("create", () => createHomeTab(newTitle.trim()), "Tab added. Upload its image and pick restaurants & dishes.").then(() =>
              setNewTitle(""),
            )
          }
          sx={{ bgcolor: GREEN, "&:hover": { bgcolor: "#0a7a3c" }, textTransform: "none", whiteSpace: "nowrap" }}
        >
          Add tab ({tabs.length}/{maxTabs})
        </Button>
      </Stack>
      )}

      {entriesFor && (
        <EntriesDialog
          tab={entriesFor}
          onToast={onToast}
          onClose={() => {
            setEntriesFor(null);
            load();
          }}
        />
      )}

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Delete “{confirmDelete?.title}”?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14 }}>
            The tab and its restaurant & dish list are removed from the app. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} sx={{ textTransform: "none" }}>
            Keep tab
          </Button>
          <Button
            color="error"
            sx={{ textTransform: "none" }}
            onClick={() => {
              const t = confirmDelete!;
              setConfirmDelete(null);
              run(`del-${t.id}`, () => deleteHomeTab(t.id), "Tab deleted");
            }}
          >
            Delete tab
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HomeTabsManager;
